import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { AlertTitle, Grid, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    PageWrapper,
    HelixLoader,
    UpdatePageHeader,
    ConfirmationModal,
    CancelModal,
} from 'components';
import { useAlert, useAuth, usePermission } from 'core/contexts';
import {
    Dictionary,
    DictionaryAttribute,
    DictionaryDetail,
    DictionaryIndex,
    IFormInputs,
    initializePropertiesList,
    possiblyNavigateToMetadataSearch,
    validationsMessages,
} from 'lib';
import InvalidPermissionsDialog from '../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { checkForInvalidProperties } from '../../../components/paired-input-selection-widget/paired-input-selection-helper';
import { DICTIONARY_PATHS } from '../../../constants/dictionary-paths';
import {
    useDictionaryList,
    useDropChain,
    useSaveDictionaryJob,
    useTruncateChain,
} from '../../../hooks';
import { AttributeAssociationItem } from '../../edge-definitions/edge-definition-helper';
import { findErrorStateInNestedTree } from '../components/attributes/attribute-helper';
import {
    dictionaryDetailSchema,
    prepForSave,
    dictionaryToastError,
    dictionaryTabs,
    DictionaryTabs,
    styles,
    dictionaryDetailsDefaults,
    DetailsTabsErrorMessage,
    AttributesTabsErrorMessage,
} from './dictionary-manager-helper';
import {
    DictionaryManagerAttributeList,
    DictionaryManagerDetails,
} from '../components';
import { useAttributeList } from '../contexts/AttributeListContext';
import { useIndexList } from '../contexts/IndexListContext';
import { DictionaryManagerIndexList } from '../components/indexes/DictionaryManagerIndexList';
import { styles as confirmationModalStyles } from 'components/confirmation-modal/confirmation-modal-helper';
import { JOB_MANAGER_PATHS } from 'constants/job-manager-paths';
import { TabItemsType } from 'components/landing-page-wrapper/components/TabComponent';
interface DictionaryManagerProps {
    dictionaryData: any;
    isLoading: boolean;
    isEdit: boolean;
    isPredicted: boolean;
}

export const DictionaryManager = (
    props: DictionaryManagerProps
): React.JSX.Element => {
    const { dictionaryData, isLoading, isEdit, isPredicted } = props;
    const { mutate: truncateChain } = useTruncateChain();
    const { getToken } = useAuth();
    const { showAlert, hideAlert } = useAlert();
    const [searchParams] = useSearchParams();
    const { mutate: saveDictionaryJob } = useSaveDictionaryJob({ isEdit });
    const { mutate: dropChain } = useDropChain();
    const navigate = useNavigate();
    const { dictionaryId } = useParams();
    const { initAttributeList, attributes } = useAttributeList();
    const { initIndexList, indexes } = useIndexList();
    const [details, setDetails] = useState<DictionaryDetail>();
    const [selectedTab, setSelectedTab] = useState<DictionaryTabs>('Details');
    const { data: dictionaries } = useDictionaryList();
    const { hasPermissions } = usePermission();
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [isChangesUnsaved, setIsChangesUnsaved] = useState<boolean>(false);
    const [buttonLoading, setButtonLoading] = useState<boolean>(false);
    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [truncateChainModalOpen, setTruncateChainModalOpen] =
        useState<boolean>(false);
    const [openUnsavedChangesModal, setOpenUnsavedChangesModal] =
        useState<boolean>(false);
    const [tabItems, setTabItems] = useState<TabItemsType[]>(dictionaryTabs);
    const [propertiesAssociationList, setPropertiesAssociationList] = useState<
        AttributeAssociationItem[]
    >([]);

    const handlePropertyListChanged = (
        list: AttributeAssociationItem[]
    ): void => {
        setPropertiesAssociationList(list);
    };

    const initializeProperties = (properties: any): void => {
        const propertiesList = initializePropertiesList(properties);
        handlePropertyListChanged(propertiesList);
    };

    // Successful loading of the dictionary Data
    const onLoadSuccess = (dictionary: Dictionary): void => {
        const {
            name,
            description,
            groupName,
            undefinedAttributesAction,
            properties,
            layoutMetadata,
        } = dictionary;
        setDetails({
            name: name,
            description: description,
            groupName: groupName,
            undefinedAttributesAction: undefinedAttributesAction,
            layoutMetadata: layoutMetadata,
        });
        initializeProperties(properties);
        initAttributeList(dictionary?.attributes || []);
        initIndexList(dictionary?.indexes || []);
        setSelectedTab('Details');
    };

    useEffect(() => {
        onLoadSuccess(dictionaryData);
    }, [dictionaryData]); // eslint-disable-line react-hooks/exhaustive-deps

    const {
        control,
        setValue,
        watch,
        formState: { errors },
        setError,
    } = useForm<IFormInputs>({
        mode: 'all',
        resolver: yupResolver(dictionaryDetailSchema),
        defaultValues: dictionaryDetailsDefaults,
    });

    // check if the attribute list is valid when is initially loaded.
    const isErrorState: boolean = findErrorStateInNestedTree(attributes);

    const removeDetailsTabError = (): void => {
        setTabItems((prevItems) => {
            return prevItems.map((tab) => {
                if (tab.label === 'Details') {
                    return { ...tab, isError: false };
                }
                return tab;
            });
        });
    };

    useEffect(() => {
        if (!isErrorState) {
            setTabItems((prevItems) => {
                return prevItems.map((tab) => {
                    if (tab.label === 'Attributes') {
                        return { ...tab, isError: false };
                    }
                    return tab;
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isErrorState]);

    const handleDictionaryNavigation = (): void => {
        possiblyNavigateToMetadataSearch(
            navigate,
            DICTIONARY_PATHS.LANDING_PAGE,
            searchParams
        );
    };

    const onCancel = (): void => {
        if (isChangesUnsaved) {
            setOpenCancelModal(true);
        } else {
            handleDictionaryNavigation();
        }
    };

    const displayDictionaryError = (
        dictionaryToastTitle: string,
        dictionaryToastMsg: string
    ): void => {
        showAlert({
            severity: 'error',
            variant: 'filled',
            closable: true,
            onClose: hideAlert,
            children: (
                <>
                    <AlertTitle>{dictionaryToastTitle}</AlertTitle>
                    {dictionaryToastMsg}
                </>
            ),
        });
    };

    const getFormData = (): FormData => {
        const dict: Dictionary = {
            name: details?.name as string,
            description: details?.description,
            groupName: details?.groupName,
            properties: propertiesAssociationList.reduce(
                (acc: any, item: AttributeAssociationItem) => {
                    acc[item.attributeAssociation.leftSideValue] =
                        item.attributeAssociation.rightSideValue;
                    return acc;
                },
                {}
            ),
            undefinedAttributesAction: details?.undefinedAttributesAction,
            layoutMetadata: {
                color: details?.layoutMetadata?.color as string,
            },
            attributes: prepForSave(attributes) as DictionaryAttribute[],
            indexes: prepForSave(indexes) as DictionaryIndex[],
        };

        const dictString = JSON.stringify(dict, null, 2);
        const blob = new Blob([dictString], { type: 'application/json' });

        const name = crypto.randomUUID();
        const fileName = `${name}.json`;
        const file = new File([blob], fileName);

        const formData = new FormData();
        formData.append('file', file);
        return formData;
    };

    const handleDelayedDictionaryNavigation = (): void => {
        // a delay of 1 sec is added to reduce any inconsistencies we get in Dictionary list from the job scheduling after creating a dictionary
        setTimeout(() => {
            handleDictionaryNavigation();
        }, 1000);
    };

    const possiblySaveDictionary = (): void => {
        const permission: boolean = hasPermissions([
            'createMetadata',
            'editMetadata',
        ]);

        if (permission) {
            const formData = getFormData();

            let isDuplicatedDictionary = false;
            if (!isEdit) {
                isDuplicatedDictionary = (dictionaries?.data ?? []).some(
                    (dictionary) => dictionary.name === details?.name
                );
            }
            if (isDuplicatedDictionary) {
                displayDictionaryError(
                    dictionaryToastError.duplicatedDictionary.msgTitle,
                    dictionaryToastError.duplicatedDictionary.messageText
                );
                return;
            }
            setButtonLoading(true);
            saveDictionaryJob(
                { token: getToken(), job: formData },
                {
                    onSuccess: () => {
                        setIsChangesUnsaved(false);
                        setButtonLoading(false);
                        handleDelayedDictionaryNavigation();
                    },
                    onError: () => {
                        setButtonLoading(false);
                    },
                }
            );
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const showTabError = (tabLabel: string, errorMessage: string): void => {
        showAlert({
            severity: 'error',
            variant: 'filled',
            closable: true,
            onClose: hideAlert,
            children: errorMessage,
        });
        setTabItems((prevItems) =>
            prevItems.map((tab) =>
                tab.label === tabLabel ? { ...tab, isError: true } : tab
            )
        );
    };

    const isValidCustomValidation = (): boolean => {
        const isNameValid = !!(details?.name ?? '');
        const { isListValid, updatedList } = checkForInvalidProperties(
            propertiesAssociationList
        );

        if (isErrorState) {
            showTabError('Attributes', AttributesTabsErrorMessage);
        }
        if (!isNameValid) {
            setError('name', {
                message: validationsMessages.required,
            });
            showTabError('Details', DetailsTabsErrorMessage);
        }
        if (!isListValid) {
            showTabError('Details', DetailsTabsErrorMessage);
            setPropertiesAssociationList(updatedList);
        }

        return isNameValid && isListValid && !isErrorState;
    };

    const possiblyRemoveDetailsTabError = (): void => {
        if (!Object.keys(errors).length) {
            removeDetailsTabError();
        }
    };

    const onSaveButtonClick = (): void => {
        if (isValidCustomValidation()) {
            possiblyRemoveDetailsTabError();
            possiblySaveDictionary();
        }
    };

    const handleDeleteModal = (): void => {
        dropChain(
            { token: getToken(), chain: dictionaryId ?? '' },
            {
                onSuccess: () => {
                    handleDelayedDictionaryNavigation();
                },
            }
        );
    };

    const handleDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handleCancelModalConfirmation = (): void => {
        setOpenCancelModal(false);
        navigate(DICTIONARY_PATHS.LANDING_PAGE);
    };

    const handleCancelModal = (): void => {
        setOpenCancelModal(false);
    };

    const openTruncationModal = (): void => {
        setTruncateChainModalOpen(true);
    };

    const closeTruncationModal = (): void => {
        setTruncateChainModalOpen(false);
    };

    const handleTruncateChainJob = (): void => {
        truncateChain(
            {
                token: getToken(),
                chain: dictionaryId ?? '',
            },
            {
                onSuccess: () => {
                    setTruncateChainModalOpen(false);
                    setTimeout(() => {
                        navigate(DICTIONARY_PATHS.LANDING_PAGE);
                    }, 500);
                },
                onError: () => {
                    setTruncateChainModalOpen(false);
                },
            }
        );
    };

    const handleSaveBeforeUploadFile = (): void => {
        const permission: boolean = hasPermissions([
            'createMetadata',
            'editMetadata',
        ]);
        if (!permission) {
            setDisplayPermissionDialog(true);
            return;
        }
        if (isValidCustomValidation()) {
            setButtonLoading(true);
            const formData = getFormData();
            saveDictionaryJob(
                { token: getToken(), job: formData },
                {
                    onSuccess: () => {
                        setIsChangesUnsaved(false);
                        setButtonLoading(false);
                        setTimeout(() => {
                            navigate(
                                JOB_MANAGER_PATHS.CREATE_UPLOAD_DICTIONARY_JOB,
                                {
                                    state: dictionaryId,
                                }
                            );
                        }, 50);
                    },
                    onError: () => {
                        setButtonLoading(false);
                    },
                }
            );
        }
    };

    const onUploadFileButtonClick = (): void => {
        if (isValidCustomValidation()) {
            handleSaveBeforeUploadFile();
        }
    };

    const handleUploadFile = (): void => {
        if (isChangesUnsaved) {
            setOpenUnsavedChangesModal(true);
        } else {
            setOpenUnsavedChangesModal(false);
            navigate(JOB_MANAGER_PATHS.CREATE_UPLOAD_DICTIONARY_JOB, {
                state: dictionaryId,
            });
        }
    };

    const handleUnsavedChangesModal = (): void => {
        setOpenUnsavedChangesModal(false);
    };

    const onTabChange = (event: React.SyntheticEvent, newValue: any): void => {
        if (event) {
            setSelectedTab(newValue);
        }
    };

    const renderTabComponents = (): React.JSX.Element => {
        if (selectedTab === 'Details') {
            return (
                <DictionaryManagerDetails
                    details={details}
                    setDetails={setDetails}
                    isEdit={isEdit}
                    isPredicted={isPredicted}
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    errors={errors}
                    setIsChangesUnsaved={setIsChangesUnsaved}
                    propertiesAssociationList={propertiesAssociationList}
                    handlePropertyListChanged={handlePropertyListChanged}
                />
            );
        } else if (selectedTab === 'Attributes') {
            return (
                <DictionaryManagerAttributeList
                    setIsChangesUnsaved={setIsChangesUnsaved}
                />
            );
        } else {
            return (
                <DictionaryManagerIndexList
                    setIsChangesUnsaved={setIsChangesUnsaved}
                />
            );
        }
    };

    if (isLoading) {
        return <HelixLoader />;
    }

    return (
        <PageWrapper
            title={dictionaryId ? `${dictionaryId}` : 'Dictionary Name'}
            includeBreadcrumbs={true}
        >
            {details ? (
                <>
                    <UpdatePageHeader
                        headingText={'Dictionary'}
                        deleteMenuItemText={'Delete Dictionary'}
                        handleSaveButton={onSaveButtonClick}
                        handleCancelButton={onCancel}
                        isLoading={isLoading || buttonLoading}
                        isTabsComponentVisible={true}
                        menuWidth={styles.menuStyles}
                        isChangesUnsaved={isChangesUnsaved}
                        openCancelModal={openCancelModal}
                        handleCancelModalConfirmation={
                            handleCancelModalConfirmation
                        }
                        handleCancelModal={handleCancelModal}
                        handleTruncateChain={openTruncationModal}
                        handleUploadFile={handleUploadFile}
                        unsavedChangesConfirmationText={
                            'This dictionary has unsaved changes. Are you sure you want to close without saving? You will lose all changes.'
                        }
                        isMenuComponentVisible={isEdit}
                        isEditDictionary={isEdit}
                        tabItems={tabItems}
                        onTabChange={onTabChange}
                        selectedTab={selectedTab}
                        tabsStyles={styles.tabsStyles}
                        tabItemStyles={styles.tabItemStyles}
                        confirmationModalConfig={{
                            confirmationKey: dictionaryId,
                            confirmationButtonText: 'Delete Dictionary',
                            confirmationInputLabelText: `Type ${dictionaryId} to confirm`,
                            confirmationHeadingText:
                                'Confirm Dictionary Deletion',
                            handleConfirmation: handleDeleteModal,
                            confirmationCancelButtonText: 'Cancel',
                            confirmationSubHeadingText: (
                                <Typography>
                                    By typing{' '}
                                    <Typography
                                        component='span'
                                        sx={
                                            confirmationModalStyles.textBoldStyles
                                        }
                                    >
                                        ‘{dictionaryId}’
                                    </Typography>{' '}
                                    below, you understand that you are
                                    permanently deleting this dictionary. This
                                    action{' '}
                                    <Typography
                                        component='span'
                                        sx={
                                            confirmationModalStyles.textBoldStyles
                                        }
                                    >
                                        CANNOT
                                    </Typography>{' '}
                                    be undone.
                                </Typography>
                            ),
                        }}
                    />
                    <Grid sx={styles.renderTabComponentContainerStyles}>
                        {renderTabComponents()}
                    </Grid>
                    {truncateChainModalOpen && (
                        <ConfirmationModal
                            confirmationModal={truncateChainModalOpen}
                            handleConfirmationModal={closeTruncationModal}
                            confirmationKey={dictionaryId}
                            confirmationButtonText={'Truncate Chain'}
                            confirmationHeadingText={'Confirm Chain Truncation'}
                            confirmationInputLabelText={`Type ${dictionaryId} below`}
                            handleConfirmation={handleTruncateChainJob}
                            confirmationSubHeadingText={
                                <Typography>
                                    By typing{' '}
                                    <Typography
                                        component='span'
                                        sx={
                                            confirmationModalStyles.textBoldStyles
                                        }
                                    >
                                        ‘{dictionaryId}’
                                    </Typography>{' '}
                                    below, you understand that you are
                                    permanently deleting this data on this
                                    chain. This action{' '}
                                    <Typography
                                        component='span'
                                        sx={
                                            confirmationModalStyles.textBoldStyles
                                        }
                                    >
                                        CANNOT
                                    </Typography>{' '}
                                    be undone.
                                </Typography>
                            }
                        />
                    )}
                    {openUnsavedChangesModal && (
                        <CancelModal
                            cancelModal={openUnsavedChangesModal}
                            handleCancelModal={handleUnsavedChangesModal}
                            cancelButtonText='Save and Upload File'
                            cancelHeadingText='You have unsaved changes.'
                            cancelSubHeadingText='This dictionary has unsaved changes. You must save changes before you can upload a file.'
                            cancelModalText='Go Back'
                            handleCancel={onUploadFileButtonClick}
                        />
                    )}
                    {displayPermissionDialog && (
                        <InvalidPermissionsDialog
                            open={displayPermissionDialog}
                            onClose={handleDialogClose}
                        />
                    )}
                </>
            ) : (
                <></>
            )}
        </PageWrapper>
    );
};
